<template>
  <div
    class="ui vertical menu"
    :style="mainStyle"
    @mouseenter="mouseenter"
    @mouseleave="mouseleave"
    id="changeLogList"
    v-on-clickaway="onClickOutside"
  >
    <div class="ui item form">
      <div class="fields">
        <div class="ui nine wide field">
          <label for="search">{{ $t('SEARCH') }}</label>
          <input
            id="search"
            :disabled="!changes || changes.length === 0"
            :placeholder="$t('SEARCH')"
            v-model="searchTerm"
            autocomplete="off"
          />
        </div>
        <div class="ui five wide field">
          <label>{{ $t('SINCE') }}</label>
          <v-date-picker
            name="since"
            mode="single"
            :max-date="new Date()"
            v-model="sinceDate"
            :class="{ disabled: !changes || changes.length === 0 }"
          />
        </div>
        <div class="ui two wide field taggedField">
          <label for="tags">{{ $t('TAGGED_ONLY') }}</label>
          <div class="ui fitted checkbox">
            <input
              id="tags"
              type="checkbox"
              v-model="onlyTagged"
              :disabled="!changes || changes.length === 0"
            />
            <label></label>
          </div>
        </div>
      </div>
    </div>
    <div class="item messageItem" v-if="isDoubleSelect && filteredChanges.length > 0">
      <div class="ui info message compact middle aligned selectChangesMsg">
        <i class="hand pointer icon" />
        {{ $t('CHANGES_SELECT') }}
      </div>
    </div>
    <div class="changeScroller" :style="innerStyle">
      <div
        class="item"
        v-if="(!changes || changes.length === 0) && showNoChanges"
        v-html="$t('NO_CHANGES')"
      />
      <template v-else>
        <Spinny :promise="changesPromise" />
        <div
          class="gradient-box"
          v-if="showGradientBox"
          :style="{ top: gradientBoxOffset, height: gradientBoxHeight }"
        ></div>
        <single-change
          v-for="(change, index) in filteredChanges"
          :key="change.identifier"
          :index="index"
          :change="change"
          :onClick="onChangeClick"
          :tagChange="tagChange"
          :removeTag="removeTag"
          :revertToChange="revertToChange"
          :isEditDisabled="isEditDisabled"
          :class="highlightClasses(index)"
          :user="userNameEmailMap[change.context.author]"
          ref="items"
        />
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@charset "utf-8";
@import '../../scss/variables';

#changeLogList {
  width: auto; // Override fomantic menu width in this case
  margin: 0;

  .changeScroller {
    overflow-y: auto;
    position: relative;
  }

  .messageItem {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .selectChangesMsg {
    padding: 10px;
  }

  .taggedField {
    white-space: pre-line;
  }

  .ui.bottom.tabular.menu .active.item {
    border-radius: 0;
  }

  .oldSelected {
    box-shadow: inset -3px -3px 0 3px $purple, inset 3px -3px 0 3px $purple;
    border-bottom-left-radius: 5px;
    border-bottom-right-radius: 5px;
  }

  .newSelected {
    box-shadow: inset 3px 3px 0 3px $yellow, inset -3px 3px 0 3px $yellow;
    border-top-left-radius: 5px;
    border-top-right-radius: 5px;
  }

  .gradient-box {
    position: absolute;
    left: 0;
    right: 0;
    z-index: 1;
    pointer-events: none;

    border-width: 6px;
    border-style: solid;
    border-image: linear-gradient(to bottom, $yellow, $purple) 1 100%;
  }
}
</style>

<script>
// @flow
import Vue from 'vue'
import { Toast, Changelog } from 'services'
import { Spinny } from 'ui'
import _ from 'lodash'
import SingleChange from './SingleChange'
import { mixin as clickaway } from 'vue-clickaway'

export default {
  name: 'changelog-list',
  components: { SingleChange, Spinny },
  mixins: [clickaway],
  props: {
    typename: {
      type: String,
      default: () => ''
    },
    itemId: {
      type: String,
      default: () => ''
    },
    sinceVersion: {
      type: String,
      default: () => null
    },
    revert: {
      type: Function,
      default: () => ''
    },
    editorMode: {
      type: String,
      default: () => ''
    },
    isDoubleSelect: {
      type: Boolean,
      default: () => false
    },
    mainStyle: {
      type: Object,
      default: () => {}
    },
    innerStyle: {
      type: Object,
      default: () => {}
    },
    showNoChanges: {
      type: Boolean,
      default: () => false
    },
    selectChangeOnLoad: {
      type: Boolean,
      default: () => false
    },
    isEditDisabled: {
      type: Boolean,
      default: () => false
    }
  },

  data() {
    return {
      searchTerm: '',
      onlyTagged: false,
      oldChange: {},
      newChange: {},
      changes: [],
      sinceDate: null,
      changesPromise: null,
      areChangesBeingFetched: false,
      debouncedEmit: _.debounce(() => this.emitFromChanges(), 500)
    }
  },

  computed: {
    filteredChanges() {
      let filtered = this.onlyTagged
        ? _.filter(this.changes, ch => ch.tagname !== '')
        : this.changes
      return _.filter(filtered, ch => {
        return ch && ch.tagname.indexOf(this.searchTerm || '') !== -1
      })
    },
    oldSelected() {
      return !_.isEmpty(this.oldChange)
    },
    newSelected() {
      return !_.isEmpty(this.newChange)
    },
    allUsers() {
      return this.$store.state.user.items
    },
    userNameEmailMap() {
      // Map/object to quickly find the user with the name
      return _.keyBy(this.allUsers, function(user) {
        return user.name
      })
    },
    // Gradient box computations:
    indexOfOldChange() {
      return this.oldSelected ? this.oldChange.idx : -1
    },
    indexOfNewChange() {
      return this.newSelected ? this.newChange.idx : -1
    },
    showGradientBox() {
      return (
        this.oldSelected && this.newSelected && this.indexOfOldChange > this.indexOfNewChange + 1
      )
    },
    gradientBoxOffset() {
      // Stretch box by 1 px in both directions to hide unsightly 1px rounding errors
      return this.newChange.renderedOffset + this.newChange.renderedHeight - 1 + 'px'
    },
    gradientBoxHeight() {
      return (
        this.oldChange.renderedOffset -
        this.newChange.renderedOffset -
        this.oldChange.renderedHeight +
        2 +
        'px'
      )
    }
    // End of gradient box computations
  },

  methods: {
    onClickOutside() {
      this.$emit('outsideClick')
    },
    getChanges() {
      if (!this.areChangesBeingFetched && this.itemId) {
        this.areChangesBeingFetched = true
        this.changesPromise = Changelog.getAllChanges(this.itemId, this.typename, this.sinceDate)
          .then(changes => {
            // The first change is the current version, we dont need that for single select
            this.changes = this.isDoubleSelect ? changes : _.tail(changes)
            this.makeDefaultSelection()
            this.areChangesBeingFetched = false
            return changes
          })
          .catch(e => {
            console.error(e)
            this.areChangesBeingFetched = false
          })
        return this.changesPromise
      }
    },

    makeDefaultSelection() {
      if (this.selectChangeOnLoad) {
        this.$nextTick(() => {
          let twoIdx = 1
          let [one, two] = this.filteredChanges

          if (this.sinceVersion) {
            twoIdx = _.findIndex(this.filteredChanges, c => c.identifier === this.sinceVersion)
            console.debug('found twoIdx', twoIdx)
            if (twoIdx >= 0) two = this.filteredChanges[twoIdx]
          }

          // We need to include dom measurements for efficient gradient box drawing
          const offsetHeight = _.get(this.$refs, 'items[0].$el.offsetHeight') || 0
          if (one) this.selectChange(0, one, 0, offsetHeight)
          if (two) this.selectChange(twoIdx, two, offsetHeight * twoIdx, offsetHeight)
        })
      }
    },

    onChangeClick(index, change, offsetTop, offsetHeight) {
      if (this.isNewChangeChosen(index)) {
        this.selectChange(index, change, offsetTop, offsetHeight)
      } else {
        this.unselectCurrentChange(index)
      }
    },

    selectChange(index, change, offsetTop, offsetHeight) {
      Changelog.getSpecificChange(this.itemId, change.identifier, this.typename).then(data => {
        if (this.isDoubleSelect) {
          this.handleDoubleSelect(
            index,
            change,
            data.new.descriptive,
            data.old.descriptive,
            offsetTop,
            offsetHeight
          )
        } else {
          this.handleSingleSelect(index, change, data.new)
        }
      })
    },

    handleDoubleSelect(index, change, newDescriptive, oldDescriptive, offsetTop, offsetHeight) {
      const clickedChange = {
        idx: index,
        ts: change.ts,
        newDescriptive,
        oldDescriptive,
        tag: change.tagname,
        identifier: change.identifier,
        renderedOffset: offsetTop,
        renderedHeight: offsetHeight
      }

      if (!this.newSelected) this.newChange = clickedChange
      else this.oldChange = clickedChange
      // Make sure oldChange comes before newChange
      if (this.newSelected && this.oldSelected && this.oldChange.ts > this.newChange.ts)
        [this.oldChange, this.newChange] = [this.newChange, this.oldChange]

      this.debouncedEmit()
    },

    emitFromChanges() {
      if (!this.oldSelected && !this.newSelected) this.$emit('changeChosen', {}, {})
      else if (!this.oldSelected || !this.newSelected) {
        // When only one is selected, show its delta
        const change = this.oldSelected ? this.oldChange : this.newChange
        this.$emit(
          'changeChosen',
          { descriptive: change.oldDescriptive, identifier: '', tag: '', ts: 0 },
          {
            descriptive: change.newDescriptive,
            identifier: change.identifier,
            tag: change.tag,
            ts: change.ts
          }
        )
      } else {
        // When two are selected, show the change between them
        this.$emit(
          'changeChosen',
          {
            descriptive: this.oldChange.newDescriptive,
            identifier: this.oldChange.identifier,
            tag: this.oldChange.tag,
            ts: this.oldChange.ts
          },
          {
            descriptive: this.newChange.newDescriptive,
            identifier: this.newChange.identifier,
            tag: this.newChange.tag,
            ts: this.newChange.ts
          }
        )
      }
    },

    handleSingleSelect(index, change, changeData) {
      this.oldChange = {
        idx: index,
        name: changeData.name,
        ts: change.ts,
        descriptive: changeData.descriptive,
        tag: change.tagname,
        identifier: change.identifier
      }
      this.$emit('changeChosen', this.oldChange, changeData)
    },

    unselectCurrentChange(index) {
      if (this.isNewChange(index)) {
        this.newChange = {}
      } else {
        this.oldChange = {}
      }
      this.debouncedEmit()
    },

    isNewChangeChosen(index) {
      return !this.isOldChange(index) && !this.isNewChange(index)
    },
    isOldChange(index) {
      return this.oldChange && this.oldChange.idx === index
    },
    isNewChange(index) {
      return this.newChange && this.newChange.idx === index
    },
    revertToChange(change) {
      return Changelog.getSpecificChange(this.itemId, change.identifier, this.typename).then(
        data => {
          this.revert(data.new)
            .then(() => {
              this.refreshComponentData()
            })
            .catch(error => {
              console.error(error)
              Toast.error(
                this.$i18n.t('ERR', {
                  action: this.$i18n.t('REVERT_ACTION', { thing: this.$i18n.tc('CHANGE', 2) })
                })
              )
            })
        }
      )
    },

    updateChangesWithTag(identifier, tag) {
      if (this.oldChange.identifier === identifier) this.oldChange.tag = tag
      if (this.newChange.identifier === identifier) this.newChange.tag = tag
    },

    tagChange(change) {
      if (change.tagname && change.tagname !== '') {
        Changelog.tagChange(this.itemId, change.identifier, change.tagname, this.typename)
          .then(() => {
            this.updateChangesWithTag(change.identifier, change.tagname)
            this.debouncedEmit()
            Toast.success(this.$i18n.t('TAG_CHANGED'))
          })
          .catch(error => {
            change.tagname = ''
            console.error(error)
            Toast.error(this.$i18n.t('FAILED_CHANGE_TAG'))
          })
      }
    },
    removeTag(change) {
      Changelog.tagChange(this.itemId, change.identifier, '', this.typename)
        .then(() => {
          change.tagname = ''
          this.updateChangesWithTag(change.identifier, '')
          this.debouncedEmit()
          Toast.success(this.$i18n.t('TAG_REMOVED'))
        })
        .catch(error => {
          console.error(error)
          Toast.error(this.$i18n.t('FAILED_REMOVE_TAG'))
        })
    },
    refreshComponentData() {
      this.clearChanges()
      this.sinceDate = new Date()
      this.sinceDate.setDate(this.sinceDate.getDate() - 30)
      this.getChanges()
    },
    clearChanges() {
      this.changes = []
      this.oldChange = {}
      this.newChange = {}
      this.searchTerm = ''
      this.onlyTagged = false
    },
    clearChosenChanges() {
      this.oldChange = {}
      this.newChange = {}
    },
    mouseenter() {
      this.$emit('mouseenter')
    },
    mouseleave() {
      this.$emit('mouseleave')
    },
    highlightClasses(index) {
      const isOld = this.isOldChange(index)
      const isNew = this.isNewChange(index)
      if (!this.isDoubleSelect) return { active: isOld || isNew }
      return {
        oldSelected: isOld,
        newSelected: isNew
      }
    }
  },

  beforeDestroy() {
    Changelog.clearChangesCache()
  },

  mounted() {
    this.refreshComponentData()
  },

  watch: {
    itemId: {
      handler: function() {
        this.refreshComponentData()
        Changelog.clearChangesCache()
      }
    },
    sinceDate: {
      handler: function() {
        this.clearChosenChanges()
        this.getChanges()
      }
    },
    onlyTagged() {
      this.clearChosenChanges()
      this.makeDefaultSelection()
    }
  }
}
</script>
